/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlinePencil, HiOutlineTrash, HiOutlineUsers } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import { Avatar } from "../../ui";
import type { Action, Column } from "../../ui";
import { useUserContext } from "../../context/UserContext";
import { pushOneUser } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type UserRow = {
  user_id: number | string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phonenumber?: string;
  country?: string;
  [key: string]: unknown;
};

const editRoute = "/user/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { users, count, fetchUsers, updateUserState, deleteUser } =
    useUserContext() as any;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!users) fetchUsers(page, pageSize);
  }, [users]);

  const rows = useMemo<UserRow[] | null>(() => {
    if (!Array.isArray(users)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return users as UserRow[];
    return (users as UserRow[]).filter((u) =>
      [u.email, u.phonenumber, u.first_name, u.last_name].some((field) =>
        String(field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [users, query]);

  const handleDelete = async (row: UserRow) => {
    const response: any = await deleteUser(row.user_id);
    if (response?.status === 200 && Array.isArray(users)) {
      updateUserState((users as UserRow[]).filter((u) => u.user_id !== row.user_id));
    }
  };

  const columns: Column<UserRow>[] = [
    {
      header: "Full name",
      cell: (row) => {
        const fullName =
          `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "—";
        return (
          <div className="flex items-center gap-3">
            <Avatar firstName={row.first_name} lastName={row.last_name} size="sm" tone="neutral" />
            <div className="min-w-0">
              <p className="truncate font-medium text-gray-900">{fullName}</p>
              {row.country ? (
                <p className="truncate text-[11px] text-gray-500">{row.country}</p>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      header: "Email",
      cell: (row) =>
        row.email ? (
          <span className="text-gray-700">{row.email}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      header: "Phone",
      cell: (row) =>
        row.phonenumber ? (
          <span className="text-gray-700">{row.phonenumber}</span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
  ];

  const rowActions = (row: UserRow): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneUser(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete ${row.first_name ?? "this user"}?`,
        description: "This account and its data will be permanently removed.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<UserRow>
      title="Users"
      subtitle="People using the public DoSeeAfrica site."
      icon={<HiOutlineUsers className="h-5 w-5" />}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.user_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search by name, email, or phone…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchUsers(p, ps);
        },
      }}
      empty="No users yet."
    />
  );
};

export default Overview;
